import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import {
    Firestore, collection, doc, collectionData, addDoc, updateDoc, deleteDoc, setDoc,
} from '@angular/fire/firestore';
import { serverTimestamp, DocumentReference } from 'firebase/firestore';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Project } from '../models/project.model';
import { AuthService } from './auth.service';
import { ApprovalService } from './approval.service';
import { isFieldExecutionProject } from '../utils/project-scope.utils';
import { isExcelSummaryProjectName } from '../utils/field-execution-import-parse.utils';

/**
 * Field Execution projects — stored separately from HM Decision (`projects` collection).
 */
@Injectable({ providedIn: 'root' })
export class FirestoreFieldExecutionProjectService {
    private readonly firestore = inject(Firestore);
    private readonly injector = inject(Injector);
    private readonly authService = inject(AuthService);
    private readonly approvalService = inject(ApprovalService);

    private readonly col = collection(this.firestore, 'field_execution_projects');
    private readonly hmCol = collection(this.firestore, 'projects');
    private readonly allProjects$ = collectionData(this.col, { idField: 'id' }) as Observable<Project[]>;

    private migrationDone = false;

    private injectCtx<T>(fn: () => T): T {
        return runInInjectionContext(this.injector, fn);
    }

    getProjects$(): Observable<Project[]> {
        return this.allProjects$.pipe(
            catchError(err => {
                console.error('[Field Execution] Firestore read failed (deploy rules for field_execution_projects):', err);
                return of([] as Project[]);
            })
        );
    }

    /** Move legacy FE rows out of `projects` into `field_execution_projects`. */
    async migrateFromHmCollection(hmProjects: Project[], opts?: { force?: boolean }): Promise<number> {
        if (!opts?.force && this.migrationDone) return 0;
        if (!opts?.force) this.migrationDone = true;

        const toMove = hmProjects.filter(isFieldExecutionProject);
        if (toMove.length === 0) return 0;

        await Promise.all(toMove.map(async p => {
            const { id, ...data } = p;
            const payload = {
                ...data,
                projectScope: 'field_execution' as const,
                migratedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };
            const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined));
            await this.injectCtx(() => setDoc(doc(this.col, id), clean, { merge: true }));
            await this.injectCtx(() => deleteDoc(doc(this.hmCol, id)));
        }));

        return toMove.length;
    }

    private async isOperator(): Promise<boolean> {
        return this.authService.isOperator();
    }

    private async canWriteDirectly(): Promise<boolean> {
        return this.authService.isAdmin() || this.authService.isMinister() || this.authService.isOsd();
    }

    async createProject(project: Omit<Project, 'id'>): Promise<string> {
        const clean = Object.fromEntries(
            Object.entries({ ...project, projectScope: 'field_execution' })
                .filter(([, v]) => v !== undefined)
        );

        if (await this.isOperator()) {
            const safePayload = Object.fromEntries(
                Object.entries(clean).filter(([, v]) => v !== undefined && v !== null)
            );
            await this.approvalService.enqueueChange({
                targetCollection: 'field_execution_projects',
                operation: 'create',
                payload: safePayload,
                remarks: 'Field execution project creation request',
            });
            return 'pending';
        }

        if (!(await this.canWriteDirectly()) && !this.authService.isApprover()) {
            throw new Error('Unauthorized: cannot create field execution projects.');
        }

        const ref = await this.injectCtx(() =>
            addDoc(this.col, { ...clean, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
        );
        return (ref as DocumentReference).id;
    }

    async updateProject(id: string, updates: Partial<Omit<Project, 'id'>>): Promise<void> {
        const clean = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined));

        if (await this.isOperator()) {
            const safePayload = Object.fromEntries(
                Object.entries(clean).filter(([, v]) => v !== undefined && v !== null)
            );
            await this.approvalService.enqueueChange({
                targetCollection: 'field_execution_projects',
                targetDocId: id,
                operation: 'update',
                payload: safePayload,
                remarks: 'Field execution project update request',
            });
            return;
        }

        if (!(await this.canWriteDirectly()) && !this.authService.isApprover()) {
            throw new Error('Unauthorized: cannot update field execution projects.');
        }

        await this.injectCtx(() =>
            updateDoc(doc(this.col, id), { ...clean, updatedAt: serverTimestamp() })
        );
    }

    async deleteProject(id: string): Promise<void> {
        if (await this.isOperator()) {
            await this.approvalService.enqueueChange({
                targetCollection: 'field_execution_projects',
                targetDocId: id,
                operation: 'delete',
                payload: { id },
                remarks: 'Field execution project deletion request',
            });
            return;
        }

        if (!(await this.canWriteDirectly()) && !this.authService.isApprover()) {
            throw new Error('Unauthorized: cannot delete field execution projects.');
        }

        await this.injectCtx(() => deleteDoc(doc(this.col, id)));
    }

    /** Remove mistaken Excel summary rows (TOTAL=, Grand Total, etc.) from Firestore. */
    async cleanupSummaryImportRows(projects: Project[]): Promise<number> {
        const toDelete = projects.filter(p => p.id && isExcelSummaryProjectName(p.name));
        if (toDelete.length === 0) return 0;

        let removed = 0;
        await Promise.all(
            toDelete.map(async p => {
                try {
                    await this.injectCtx(() => deleteDoc(doc(this.col, p.id)));
                    removed++;
                } catch (err) {
                    console.warn('[Field Execution] Could not remove summary row:', p.name, err);
                }
            })
        );
        if (removed > 0) {
            console.info(`[Field Execution] Removed ${removed} mistaken Excel summary row(s) (e.g. TOTAL=).`);
        }
        return removed;
    }

    /** Move NAC-tagged projects out of Dhamanagar Block into Dhamanagar NAC (FE collection). */
    async migrateNacProjects(projects: Project[]): Promise<number> {
        const nacBlocks = new Set(['dhamanagar nac', 'dhamnagar nac', 'tihidi nac', 'dhusuri nac']);
        const dhamanagarBlocks = new Set(['dhamanagar', 'dhamnagar', 'nac']);

        const toMigrate = projects.filter(p => {
            const b = (p.block || '').toLowerCase().trim();
            if (nacBlocks.has(b)) return false;
            const v = (p.village || p.loc || '').toLowerCase().trim();
            const isNacVillage =
                v === 'nac' ||
                v.includes('dnr, nac') ||
                v.includes('dhamnagar nac') ||
                v.includes('dhamanagar nac');
            return dhamanagarBlocks.has(b) && isNacVillage;
        });

        if (toMigrate.length === 0) return 0;

        await Promise.all(
            toMigrate.map(p =>
                this.updateProject(p.id, { block: 'Dhamanagar NAC', village: 'Nac' })
            )
        );
        return toMigrate.length;
    }
}
